import { useRef, useState } from "react";
import { Markdown } from "zudoku/components";
import { Badge } from "zudoku/ui/Badge";
import { Button } from "zudoku/ui/Button";
import { Input } from "zudoku/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "zudoku/ui/Select";
import * as SidecarBox from "zudoku/ui/SidecarBox";
import { SyntaxHighlight } from "zudoku/ui/SyntaxHighlight";
import type { PlaygroundOperation } from "../lib/oas";
import { CodeSamples } from "./CodeSamples";

const METHOD_COLOR: Record<string, string> = {
  GET: "bg-sky-500",
  POST: "bg-blue-600",
  PUT: "bg-amber-500",
  PATCH: "bg-amber-500",
  DELETE: "bg-red-600",
};

type RequestLogEntry = { time: string; status: number; userAgent: string };

/** Stands in for the JSON body in the error list, which is keyed by field name. */
const BODY_ERROR_KEY = "__body__";

// Recreates the ReadMe.io middle-column layout for one operation — form
// fields inline in the docs flow (not behind a click-to-open dialog), plus
// the Recent Requests / Headers / Responses sections the old site showed.
// Fully data-driven from a PlaygroundOperation (see src/lib/oas.ts), so
// applying this to all 36 operations is a loop, not per-page authoring.
export function OperationForm({ op }: { op: PlaygroundOperation }) {
  const [values, setValues] = useState<Record<string, string>>({});
  // Seeded from the spec's own `example` values so the reader edits a real
  // document instead of starting at an empty box.
  const [jsonBody, setJsonBody] = useState(op.jsonBody?.example ?? "");
  // Names of required fields that were empty when Send was last pressed,
  // plus "__body__" for an unparseable JSON body. Populated on submit only:
  // marking fields red before anyone has tried to send is just noise.
  const [errors, setErrors] = useState<string[]>([]);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const [accept, setAccept] = useState(
    op.acceptContentTypes[0] ?? "application/json",
  );
  const [log, setLog] = useState<RequestLogEntry[]>([]);
  const [openResponse, setOpenResponse] = useState<string | null>(null);
  const [result, setResult] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "done"; status: number; body: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const setField = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [name]: e.target.value }));
    if (e.target.value.trim()) {
      setErrors((prev) => prev.filter((n) => n !== name));
    }
  };

  /**
   * Required fields left blank, plus the JSON body if it doesn't parse.
   * Sending anyway only earns a 400 that reads like the docs are broken
   * rather than like the request was incomplete.
   */
  const validate = () => {
    const missing = op.fields
      .filter((f) => f.required && !(values[f.name] ?? "").trim())
      .map((f) => f.name);

    if (op.jsonBody) {
      try {
        JSON.parse(jsonBody);
      } catch {
        missing.push(BODY_ERROR_KEY);
      }
    }
    return missing;
  };

  const send = async () => {
    const invalid = validate();
    setErrors(invalid);
    if (invalid.length > 0) {
      // Jump to the first problem rather than leaving the reader to hunt
      // for the red box — the form is long enough to scroll past.
      const target = fieldRefs.current[invalid[0]];
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
      return;
    }

    setResult({ kind: "loading" });
    try {
      let path = op.path;
      const query = new URLSearchParams();
      const form = new URLSearchParams();

      for (const field of op.fields) {
        const value = values[field.name] ?? "";
        if (field.in === "path") path = path.replace(`{${field.name}}`, value);
        else if (field.in === "query") {
          if (value) query.set(field.name, value);
        } else if (field.in === "formData") {
          if (value) form.set(field.name, value);
        }
      }

      const url = `${op.server}${path}${query.toString() ? `?${query}` : ""}`;
      // JSON-bodied operations send the edited document verbatim; the rest
      // keep the urlencoded form built above.
      const contentType = op.jsonBody?.contentType ?? "application/x-www-form-urlencoded";
      const payload = op.jsonBody ? jsonBody : form.toString();
      const res = await fetch(url, {
        method: op.method,
        headers: {
          Accept: accept,
          ...(op.method !== "GET" ? { "Content-Type": contentType } : {}),
        },
        body: op.method !== "GET" ? payload : undefined,
      });
      const text = await res.text();
      let body = text;
      try {
        body = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        // not JSON — show raw text
      }
      setResult({ kind: "done", status: res.status, body });
      setLog((l) => [
        {
          time: new Date().toLocaleTimeString(),
          status: res.status,
          userAgent: navigator.userAgent.split(" ").slice(-2).join(" "),
        },
        ...l,
      ]);
    } catch (err) {
      const message =
        err instanceof TypeError
          ? "Request blocked — this endpoint's server doesn't allow browser requests from this origin yet (CORS)."
          : String(err);
      setResult({ kind: "error", message });
      setLog((l) => [
        { time: new Date().toLocaleTimeString(), status: 0, userAgent: "blocked" },
        ...l,
      ]);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Badge className={`${METHOD_COLOR[op.method] ?? "bg-slate-500"} text-white hover:${METHOD_COLOR[op.method]}`}>
          {op.method}
        </Badge>
        <code className="text-sm text-muted-foreground">
          {op.server}
          {op.path}
        </code>
      </div>
      <p className="text-muted-foreground mb-6">{op.description ?? op.summary}</p>
      <hr className="mb-8" />

      {/* Recent Requests */}
      <h2 className="text-lg font-semibold mb-3">Recent Requests</h2>
      <div className="border rounded-lg overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
            <tr>
              <th className="text-left font-medium px-4 py-2">Time</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-left font-medium px-4 py-2">User Agent</th>
            </tr>
          </thead>
          <tbody>
            {log.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center text-muted-foreground py-6">
                  Make a request to see history.
                </td>
              </tr>
            ) : (
              log.map((entry, i) => (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2">{entry.time}</td>
                  <td className="px-4 py-2">{entry.status || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{entry.userAgent}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Data */}
      <h2 className="text-lg font-semibold mb-3">
        {op.jsonBody ? "Body" : "Form Data"}
      </h2>
      <div className="border rounded-lg overflow-hidden mb-8">
        {/* Operations with a JSON body (Create campaign, Send voice call,
            the WhatsApp send, …) have nested payloads — arrays of objects —
            that a row of flat inputs can't express, so they get one editor
            pre-filled from the spec's own `example` values. */}
        {op.jsonBody && (
          <div className="p-4 border-b">
            <div className="text-sm mb-2">
              <span className="font-semibold font-mono">body</span>{" "}
              <span className="text-muted-foreground">{op.jsonBody.contentType}</span>{" "}
              <span className="text-destructive text-xs font-medium">required</span>
            </div>
            <textarea
              ref={(el) => {
                fieldRefs.current[BODY_ERROR_KEY] = el;
              }}
              value={jsonBody}
              onChange={(e) => {
                setJsonBody(e.target.value);
                setErrors((prev) => prev.filter((n) => n !== BODY_ERROR_KEY));
              }}
              spellCheck={false}
              rows={Math.min(18, jsonBody.split("\n").length + 1)}
              aria-invalid={errors.includes(BODY_ERROR_KEY)}
              aria-label="Request body"
              className={`w-full rounded-md border bg-transparent p-3 font-mono text-xs leading-relaxed outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] ${
                errors.includes(BODY_ERROR_KEY)
                  ? "border-destructive ring-destructive/20 ring-[3px]"
                  : "border-input"
              }`}
            />
            {errors.includes(BODY_ERROR_KEY) && (
              <p className="text-destructive text-xs mt-1.5">
                Body must be valid JSON
              </p>
            )}
          </div>
        )}
        {op.fields.map((field) => (
          <div
            key={`${field.in}-${field.name}`}
            className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-3 p-4 border-b last:border-b-0"
          >
            <div>
              <div className="text-sm">
                <span className="font-semibold font-mono">{field.name}</span>{" "}
                <span className="text-muted-foreground">{field.type}</span>{" "}
                {field.required && (
                  <span className="text-destructive text-xs font-medium">required</span>
                )}
              </div>
              {field.description && (
                <Markdown
                  content={field.description}
                  className="text-sm text-muted-foreground mt-1 [&_p]:my-1 [&_ul]:my-1"
                />
              )}
            </div>
            <div className="self-start">
              <Input
                ref={(el) => {
                  fieldRefs.current[field.name] = el;
                }}
                value={values[field.name] ?? ""}
                onChange={setField(field.name)}
                aria-invalid={errors.includes(field.name)}
                className={`h-9 ${
                  errors.includes(field.name)
                    ? "border-destructive ring-destructive/20 ring-[3px]"
                    : ""
                }`}
              />
              {errors.includes(field.name) && (
                <p className="text-destructive text-xs mt-1.5">
                  {field.name} is required
                </p>
              )}
            </div>
          </div>
        ))}
        <div className="p-4 bg-muted/30 flex items-center gap-3">
          {/* Deliberately always clickable: pressing Send is what surfaces
              the per-field errors and moves the cursor to the first one. A
              greyed-out button just leaves the reader guessing which of a
              dozen rows it is waiting on. */}
          <Button onClick={send} disabled={result.kind === "loading"}>
            {result.kind === "loading" ? "Sending…" : "Send"}
          </Button>
          {errors.length > 0 && (
            <p className="text-destructive text-sm">
              {errors.length === 1
                ? "1 field needs attention"
                : `${errors.length} fields need attention`}
            </p>
          )}
        </div>
      </div>

      {/* Headers */}
      {op.acceptContentTypes.length > 1 && (
        <>
          <h2 className="text-lg font-semibold mb-3">Headers</h2>
          <div className="border rounded-lg overflow-hidden mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-3 p-4">
              <div>
                <div className="text-sm">
                  <span className="font-semibold font-mono">accept</span>{" "}
                  <span className="text-muted-foreground">
                    string enum — defaults to {op.acceptContentTypes[0]}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Generated from available response content types
                </p>
                <div className="flex gap-2 mt-2">
                  {op.acceptContentTypes.map((ct) => (
                    <Badge key={ct} variant="outline" className="font-mono text-xs">
                      {ct}
                    </Badge>
                  ))}
                </div>
              </div>
              <Select value={accept} onValueChange={setAccept}>
                <SelectTrigger className="h-9 self-start">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {op.acceptContentTypes.map((ct) => (
                    <SelectItem key={ct} value={ct}>
                      {ct}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* Responses */}
      <h2 className="text-lg font-semibold mb-3">Responses</h2>
      <div className="border rounded-lg overflow-hidden">
        {op.responses.map((res) => {
          const ok = res.status.startsWith("2");
          const isOpen = openResponse === res.status;
          return (
            <div key={res.status} className="border-b last:border-b-0">
              <button
                type="button"
                onClick={() => setOpenResponse(isOpen ? null : res.status)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30"
              >
                <span
                  className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`}
                />
                <span className="font-mono font-semibold text-sm">{res.status}</span>
                {res.description && (
                  <span className="text-muted-foreground text-sm">{res.description}</span>
                )}
                <span className="ml-auto text-muted-foreground">
                  {isOpen ? "⌄" : "›"}
                </span>
              </button>
              {isOpen && res.example != null && (
                <div className="px-4 pb-4">
                  <SyntaxHighlight
                    language="json"
                    code={JSON.stringify(res.example, null, 2)}
                    className="text-xs"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>

    {/* Sidecar: example request code (updates live as Form Data fields are
        filled in) on top, the actual response from the last Send below. */}
    <div className="lg:sticky lg:top-(--padding-content-top) h-fit flex flex-col gap-4">
      <SidecarBox.Root className="not-prose">
        <SidecarBox.Head className="text-xs font-medium">Example Request</SidecarBox.Head>
        <SidecarBox.Body className="p-0 example-request-code">
          <CodeSamples op={op} values={values} jsonBody={jsonBody} />
        </SidecarBox.Body>
      </SidecarBox.Root>

      <SidecarBox.Root className="not-prose">
        <SidecarBox.Head className="text-xs font-medium">Response</SidecarBox.Head>
        <SidecarBox.Body className="p-0">
          {result.kind === "idle" && (
            <p className="p-3 text-sm text-muted-foreground">
              Click Send to see the response.
            </p>
          )}
          {result.kind === "loading" && (
            <p className="p-3 text-sm text-muted-foreground">Sending…</p>
          )}
          {result.kind === "done" && (
            <SyntaxHighlight
              embedded
              language="json"
              className="rounded-none text-xs"
              code={result.body}
            />
          )}
          {result.kind === "error" && (
            <p className="p-3 text-sm text-destructive">{result.message}</p>
          )}
        </SidecarBox.Body>
        {result.kind === "done" && (
          <SidecarBox.Footer className="text-muted-foreground text-xs">
            HTTP {result.status}
          </SidecarBox.Footer>
        )}
      </SidecarBox.Root>
    </div>
    </div>
  );
}
